import React, { FC, PropsWithChildren, Fragment, useEffect, useState, useCallback, useRef } from "react";
import CountriesSelect, { CountriesType } from "./components/CountrySelected/CountrySelected";
import { find, get } from "lodash";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppStore } from "../../redux/store";
import useCities from "../../hooks/useCities";
import { isLoggedIn } from "../../helpers/authHelper";
import useOptionsNav from "../../hooks/useOptionsNav";
import { toogleAction } from "../../redux/features/navigation";
import { getAppPropiSpaPath } from "../../helpers/realmHelpers";
import NavBarLandlord from "./NavBarLandlord";
import NavBarTenant from "./NavBarTenant";
import NavBarCustomer from "./NavBarCustomer";
import NavBarDeveloper from "./NavBarDeveloper";
import classNames from "classnames";
import DropdownPlaces from "./components/DropdownPlaces/DropdownPlaces";
import DropdownGeneric from "../DropdownGeneric/DropdownGeneric";
import ColorfulNameComponent from "./components/ColorfulName/ColorfulName";
import { IconMenu2, IconUser } from "@tabler/icons-react";
import DropdownMenuUser from "./components/NavDropdownUsers/NavDropDownUsers";
import Button from "../ui/Button";
import { Box, Drawer, Menu } from "@mui/material";
import NavBarMobile from "./NavBarMobile";
import { useLocation } from "react-router-dom";
import "./scss/NavBarMarket.scss";


const logo = "https://storage.googleapis.com/assets-us-east4-propilatam/website-assets/propi-web/v2/logo-propi.svg";
const logoWhite = "https://storage.googleapis.com/assets-us-east4-propilatam/website-assets/propi-web/v2/propi_white.svg"


export type LayoutDataType = {
  code: string;
  country: string;
  options: {
    slug: string;
    name: string;
    show: boolean;
  }[];
  nolink?: {
    incl: string | null; // "incl" significa "incluir" usarse si no conoce la url exacta
    link: string | null; // "link" significa URL exacta
    name: string;
  }[];
  transparent?: {
    incl: string | null; // "incl" significa "incluir" usarse si no conoce la url exacta
    link: string | null; // "link" significa URL exacta
    name: string;
  }[];
  countries?: CountriesType[];
}

type Props = {
  layoutData: LayoutDataType; // Datos de Contentful
  children?: JSX.Element;
  isTransparent?: boolean;
};

const NavBarMarket: FC<PropsWithChildren<Props>> = ({
  layoutData,
  children,
  isTransparent = false
}) => {

  const showBuyNow = find(get(layoutData, "options", []), { slug: 'compra-ya' });
  const showRent = find(get(layoutData, "options", []), { slug: 'alquilar' });
  const showBuy = find(get(layoutData, "options", []), { slug: 'comprar' });
  const showOwner = find(get(layoutData, "options", []), { slug: 'propietarios' });
  const showCalculator = find(get(layoutData, "options", []), { slug: 'calculadora' });
  const showMorePropi = find(get(layoutData, "options", []), { slug: 'mas' });

  // Array de rutas que requieren fullWidth
  // Agregar más rutas aquí según sea necesario
  const fullWidthRoutes = [
    "/venta/casas-y-apartamentos",
    "/bienes-raices"
  ];
  const location = useLocation();

const hideNavbarRoutes = [
  "/desarrolladora",
  "auth",
  "pago-rapido"
];

const shouldHideNavbar = hideNavbarRoutes.some(route =>
  location.pathname.includes(route)
);

  const fullWidth = fullWidthRoutes.some(route => window.location.pathname.includes(route));

  const { country } = useParams();

  const user = useSelector((store: AppStore) => store.auth);
  const { visible: showNav } = useSelector(
    (store: AppStore) => store.navigation
  );

  const { cities, citiesBuy } = useCities();

  const [navbarLogo, setNavbarLogo] = useState(logo);
  const isLogged = isLoggedIn(user);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const dispatch = useDispatch();
  const searchParams = useParams();

  const {
    OptionPropietario,
    OptionCalcula,
    OptionPropi,
  } = useOptionsNav();

  const doShowNav = () => {
    if (isLogged) {
      dispatch(toogleAction(!showNav));
    } else {
      return window.open(
        `${getAppPropiSpaPath()}/${country}/auth/login`,
        "_self"
      );
    }
  };

  const getNavBarMobile = () => {
    if (!isLogged) {
      return null;
    }
    switch (user.realm) {
      case "landlord":
        return <NavBarLandlord />;
      case "tenant":
        return <NavBarTenant />;
      case "customer":
        return <NavBarCustomer />;
      case "developer":
        return <NavBarDeveloper />;
    }
  };

  const toggleDrawer = () => {
    setOpenDrawer((state) => !state);
  }

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isLogged) {
      setAnchorEl(event.currentTarget);
    } else {
      return window.open(`/app/${country}/auth/login`, "_self");
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const isDark = !isTransparent || isScrolled;

  // Hook optimizado para throttle
  const throttleRef = useRef<NodeJS.Timeout | null>(null);

  // Handler consolidado de scroll con throttle
  const handleOptimizedScroll = useCallback(() => {
    if (throttleRef.current) {
      clearTimeout(throttleRef.current);
    }

    throttleRef.current = setTimeout(() => {
      const scrollY = window.scrollY;

      // Consolidar todas las funciones de scroll en una sola ejecución
      if (isTransparent) {
        setIsScrolled(scrollY > 50);

        // Cambiar logo según scroll en rutas transparentes
        if (scrollY <= 66) {
          setNavbarLogo(logoWhite); // Logo blanco cuando está en transparente sin scroll
        } else {
          setNavbarLogo(logo); // Logo normal cuando hace scroll
        }
      } else {
        // Para rutas no transparentes, siempre logo normal
        setIsScrolled(false);
        setNavbarLogo(logo); // Logo normal siempre
      }
    }, 16); // ~60fps throttle
  }, [isTransparent, logo, logoWhite]);

  // Efecto único para scroll
  useEffect(() => {
    // Ejecutar una vez al montar
    handleOptimizedScroll();

    // Agregar listener
    window.addEventListener("scroll", handleOptimizedScroll, { passive: true });

    // Cleanup
    return () => {
      window.removeEventListener("scroll", handleOptimizedScroll);
      if (throttleRef.current) {
        clearTimeout(throttleRef.current);
      }
    };
  }, [handleOptimizedScroll]);


  return (
    <Fragment>
      {!shouldHideNavbar && (
      <div id="navbar" className={classNames(
        "block z-50 duration-500",
        isTransparent ? 'fixed top-0 w-full' : 'sticky top-0',
        isDark ? 'bg-white' : 'bg-transparent border-b-white',
      )}>
        <div className={classNames(
          "nav active px-2 w-full py-3 lg:py-4 border-b",
          fullWidth ? 'w-full px-4 lg:px-8' : 'mx-auto max-w-2xl md:max-w-3xl lg:max-w-5xl xl:max-w-7xl',
        )}>
          <div className="w-full flex flex-wrap justify-between items-center relative">
            <div>
              <a href={`/${country}`} className="flex items-center">
                <img
                  src={navbarLogo}
                  className="mr-3 h-6 sm:h-9"
                  alt="Logo"
                />
              </a>
            </div>

            <div
              className="hidden justify-between items-center w-full lg:flex lg:w-auto lg:order-1 relative"
              id="mobile-menu-4"
            >
              {/* Dropdowns */}
              <div className="flex justify-end ">
                <ul className="flex gap-0.5 mt-4 flex-row md:mt-0 md:text-sm md:font-medium">
                  {get(showRent, "show", false) && (<li><DropdownPlaces title={get(showRent, "name", "Alquilar")} citiesData={cities || []} isDark={isDark} /></li>)}
                  {get(showBuy, "show", false) && (<li><DropdownPlaces title={get(showBuy, "name", "Comprar")} citiesData={citiesBuy || []} isDark={isDark} /></li>)}
                  {get(showOwner, "show", false) && (<li><DropdownGeneric title={get(showOwner, "name", "Propietarios")} items={OptionPropietario} isDark={isDark} /></li>)}
                  {get(showCalculator, "show", false) && (<li><DropdownGeneric title={get(showCalculator, "name", "Simula y Calcula")} items={OptionCalcula} isDark={isDark} /></li>)}
                  {get(showMorePropi, "show", false) && (<li><DropdownGeneric title={get(showMorePropi, "name", "Más de Propi")} items={OptionPropi} isDark={isDark} /></li>)}
                </ul>
              </div>

              {/* Countries Select */}
              {!searchParams?.id && !searchParams?.code && (
                <div className={classNames(isDark ? "text-blackBase" : "text-white")}>
                  <CountriesSelect isDark={isDark} countries={get(layoutData, "countries", []) as CountriesType[]} />
                </div>
              )}

              {/* User Auth */}
              <div className="flex mr-3">
                <button
                  onClick={doShowNav}
                  type="button"
                  className="button-auth focus:outline-none"
                >
                  <img
                    className="w-5 mr-2"
                    src="https://storage.googleapis.com/assets-us-east4-propilatam/website-assets/propi-web/menu.svg"
                    alt=""
                  />
                  {isLogged ? (
                    <>
                      {user?.avatar !== null ? (
                        <img
                          className="rounded-full w-7 h-7"
                          src={user?.avatar}
                          alt=""
                        />
                      ) : (
                        <ColorfulNameComponent
                          firstName={user?.name}
                          lastName={user?.last_name}
                        />
                      )}
                    </>
                  ) : (
                    <div className="flex justify-center items-center w-6 h-6 rounded-full border border-[#212121]">
                      <IconUser width={18} height={18} />
                    </div>
                  )}
                </button>
                <DropdownMenuUser />
              </div>

              {get(showBuyNow, "show", false) && (
                <Button href={`/${country}/venta/casas-y-apartamentos/proyectos`} as="link" variant="primary">
                  {get(showBuyNow, "name", "Compra ya")}
                </Button>
              )}

            </div>
            <div className="flex lg:hidden justify-end items-center gap-2" >
              {get(showBuyNow, "show", false) && (
                <Button href={`/${country}/venta/casas-y-apartamentos/proyectos`} as="link" variant="primary" size="sm">
                  {get(showBuyNow, "name", "Compra ya")}
                </Button>
              )}
              <Button onClick={handleClick} variant="transparent" size="md" as="icon"
                className={classNames(
                  "[&>div]:hover:border-primary [&>div]:active:border-primary [&>div]:focus:border-primary",
                  isDark
                    ? "[&>div]:border-[#212121] "
                    : "text-white border-white"
                )}
              >
                {
                  isLogged ? (
                    <>
                      {user?.avatar !== null ? (
                        <img
                          className="rounded-full w-7 h-7"
                          src={user?.avatar}
                          alt=""
                        />
                      ) : (
                        <ColorfulNameComponent
                          firstName={user?.name}
                          lastName={user?.last_name}
                        />
                      )}
                    </>
                  ) : (
                    <div className="flex justify-center items-center w-6 h-6 rounded-full border">
                      <IconUser width={18} height={18} />
                    </div>
                  )}
              </Button>
              <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: "visible",
                    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.08))",
                    mt: 1.5,
                    paddingRight: 2,
                    paddingLeft: 2,
                    "& .MuiAvatar-root": {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                    "&::before": {
                      content: '""',
                      display: "block",
                      position: "absolute",
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: "background.paper",
                      transform: "translateY(-50%) rotate(45deg)",
                      zIndex: 0,
                    },
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                {getNavBarMobile()}
              </Menu>
              <div className={classNames(isDark ? "[&>div]:border-[#212121]" : "text-white border-white")}>
                <Button onClick={toggleDrawer} variant="transparent" size="md" as="icon" className="!text-inherit hover:!text-primary active:!text-primary focus:!text-primary">
                  <IconMenu2 />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {children}

        <Drawer
          anchor="right"
          open={openDrawer}
          onClose={toggleDrawer}
          className="w-full mobile-menu-custom"
        >
          <Box sx={{ width: "100%", height: "100%", backgroundColor: "transparent" }} role="presentation">
            <NavBarMobile onAction={toggleDrawer} countries={get(layoutData, "countries", []) as CountriesType[]} />
          </Box>
        </Drawer>
      </div >
      )}
    </Fragment >
  );
}

export default NavBarMarket;